// src/contexts/Web3Context.js
import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { ethers } from 'ethers';
import axios from 'axios';

const Web3Context = createContext();

export const useWeb3 = () => useContext(Web3Context);

export const Web3Provider = ({ children }) => {
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [userTokens, setUserTokens] = useState([]);
  const [isOasysNetwork, setIsOasysNetwork] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  // OASYSネットワークのチェーンID
  const OASYS_CHAIN_ID = parseInt(process.env.REACT_APP_OASYS_CHAIN_ID || '248');

  // OASYSネットワーク設定
  const OASYS_NETWORK = {
    chainId: `0x${OASYS_CHAIN_ID.toString(16)}`,
    chainName: 'OASYS Mainnet',
    nativeCurrency: {
      name: 'OAS',
      symbol: 'OAS',
      decimals: 18,
    },
    rpcUrls: [process.env.REACT_APP_OASYS_RPC_URL || 'https://rpc.mainnet.oasys.games'],
    blockExplorerUrls: ['https://explorer.oasys.games'],
  };

  // アドレスをチェックサム形式に変換する関数
  const toChecksumAddress = useCallback(address => {
    if (!address) return null;
    try {
      return ethers.utils.getAddress(address);
    } catch (error) {
      console.error('Invalid address format:', error);
      return address; // 変換に失敗した場合は元のアドレスを返す
    }
  }, []);

  // 管理者権限のチェック - useCallbackを使用して依存関係を適切に管理
  const checkAdminStatus = useCallback(
    async address => {
      if (!address) {
        setIsAdmin(false);
        return;
      }

      try {
        const checksumAddress = toChecksumAddress(address);
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/admin/check/${checksumAddress}`
        );
        setIsAdmin(response.data.isAdmin === true);
      } catch (error) {
        console.error('管理者権限チェックエラー:', error);
        setIsAdmin(false);
      }
    },
    [toChecksumAddress]
  );

  // ユーザーが発行したトークンを取得 - useCallbackを使用して依存関係を適切に管理
  const fetchUserTokens = useCallback(
    async userAddress => {
      if (!userAddress) return;

      try {
        // アドレスをチェックサム形式に変換
        const checksumAddress = toChecksumAddress(userAddress);

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/tokens/issuer/${checksumAddress}`
        );
        setUserTokens(response.data);
      } catch (error) {
        console.error('トークン取得エラー:', error);
        setUserTokens([]);
      }
    },
    [toChecksumAddress]
  );

  // ウォレットの初期化チェック
  useEffect(() => {
    const checkConnection = async () => {
      if (window.ethereum) {
        try {
          // 既に接続済みのアカウントがあるか確認
          const accounts = await window.ethereum.request({ method: 'eth_accounts' });

          if (accounts.length > 0) {
            const provider = new ethers.providers.Web3Provider(window.ethereum);
            const signer = provider.getSigner();
            const network = await provider.getNetwork();

            // アドレスをチェックサム形式に変換
            const checksumAddress = toChecksumAddress(accounts[0]);

            setAccount(checksumAddress);
            setProvider(provider);
            setSigner(signer);
            setChainId(network.chainId);
            setIsOasysNetwork(network.chainId === OASYS_CHAIN_ID);

            // 管理者権限をチェック
            await checkAdminStatus(checksumAddress);

            // ユーザートークンを取得
            fetchUserTokens(checksumAddress);
          }
        } catch (error) {
          console.error('初期化エラー:', error);
          setError('ウォレットの初期化中にエラーが発生しました');
        }
      }
    };

    checkConnection();
  }, [OASYS_CHAIN_ID, checkAdminStatus, fetchUserTokens, toChecksumAddress]);

  // イベントリスナーの設定
  useEffect(() => {
    if (window.ethereum) {
      const handleAccountsChanged = async accounts => {
        if (accounts.length === 0) {
          // ユーザーが切断した
          setAccount(null);
          setSigner(null);
          setIsAdmin(false);
        } else {
          // アドレスをチェックサム形式に変換
          const checksumAddress = toChecksumAddress(accounts[0]);

          setAccount(checksumAddress);

          if (provider) {
            const signer = provider.getSigner();
            setSigner(signer);

            // 管理者権限をチェック
            await checkAdminStatus(checksumAddress);

            // ユーザートークンを取得
            fetchUserTokens(checksumAddress);
          }
        }
      };

      const handleChainChanged = async chainIdHex => {
        const chainIdDecimal = parseInt(chainIdHex, 16);
        setChainId(chainIdDecimal);
        setIsOasysNetwork(chainIdDecimal === OASYS_CHAIN_ID);

        // プロバイダーを更新
        const provider = new ethers.providers.Web3Provider(window.ethereum);
        setProvider(provider);

        if (account) {
          setSigner(provider.getSigner());

          // 管理者権限をチェック（チェーンが変わっても実行）
          await checkAdminStatus(account);

          // ユーザートークンを取得
          fetchUserTokens(account);
        }
      };

      const handleDisconnect = () => {
        setAccount(null);
        setSigner(null);
        setIsAdmin(false);
      };

      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);
      window.ethereum.on('disconnect', handleDisconnect);

      return () => {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
        window.ethereum.removeListener('disconnect', handleDisconnect);
      };
    }
  }, [provider, account, OASYS_CHAIN_ID, checkAdminStatus, fetchUserTokens, toChecksumAddress]);

  // ウォレット接続
  const connectWallet = async () => {
    if (!window.ethereum) {
      setError('MetaMaskまたは互換性のあるウォレットがインストールされていません');
      return false;
    }

    try {
      setIsConnecting(true);
      setError(null);

      // ウォレットに接続要求
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const signer = provider.getSigner();
      const network = await provider.getNetwork();

      // アドレスをチェックサム形式に変換
      const checksumAddress = toChecksumAddress(accounts[0]);

      setAccount(checksumAddress);
      setProvider(provider);
      setSigner(signer);
      setChainId(network.chainId);
      setIsOasysNetwork(network.chainId === OASYS_CHAIN_ID);

      // 管理者権限をチェック
      await checkAdminStatus(checksumAddress);

      // ユーザートークンを取得
      fetchUserTokens(checksumAddress);

      return true;
    } catch (error) {
      console.error('接続エラー:', error);
      setError('ウォレット接続中にエラーが発生しました');
      return false;
    } finally {
      setIsConnecting(false);
    }
  };

  // ネットワーク切替
  const switchToOasysNetwork = async () => {
    if (!window.ethereum) {
      setError('MetaMaskまたは互換性のあるウォレットがインストールされていません');
      return false;
    }

    try {
      // まずはネットワーク切替を試みる
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: OASYS_NETWORK.chainId }],
      });

      return true;
    } catch (error) {
      // ネットワークがまだ追加されていない場合は追加する
      if (error.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [OASYS_NETWORK],
          });

          return true;
        } catch (addError) {
          console.error('ネットワーク追加エラー:', addError);
          setError('OASYSネットワークの追加に失敗しました');
          return false;
        }
      } else {
        console.error('ネットワーク切替エラー:', error);
        setError('OASYSネットワークへの切替に失敗しました');
        return false;
      }
    }
  };

  // 署名の実行
  const signMessage = async message => {
    if (!signer) {
      throw new Error('ウォレットが接続されていません');
    }

    try {
      const signature = await signer.signMessage(message);
      return signature;
    } catch (error) {
      console.error('署名エラー:', error);
      throw new Error('メッセージの署名に失敗しました');
    }
  };

  // 切断
  const disconnect = () => {
    setAccount(null);
    setSigner(null);
    setUserTokens([]);
    setIsAdmin(false);
  };

  const value = {
    account,
    provider,
    signer,
    chainId,
    isConnecting,
    error,
    userTokens,
    isOasysNetwork,
    isAdmin,
    connectWallet,
    switchToOasysNetwork,
    signMessage,
    disconnect,
    toChecksumAddress, // チェックサム変換関数を外部に公開
  };

  return <Web3Context.Provider value={value}>{children}</Web3Context.Provider>;
};

export default Web3Context;
